import { useRef, useState } from 'react'
import Button from '@/components/ui/button'
import CountBadge from '@/components/ui/count-badge'
import NotificationItem from '@/components/companies/header/notifications/notification-item'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCrmStore } from '@/stores/crm-store-provider'
import { NOTIFICATIONS } from '@/constants/notifications'
import BellIcon from '@/assets/images/companies/header/bell.svg'

type Filter = 'all' | 'unread'

export default function Notifications() {
    const [open, setOpen] = useState(false)
    const [filter, setFilter] = useState<Filter>('all')
    const navigated = useRef(false)
    const companies = useCrmStore(state => state.companies)
    const unreadIds = useCrmStore(state => state.unreadNotificationIds)
    const markRead = useCrmStore(state => state.markNotificationRead)
    const markAllRead = useCrmStore(state => state.markAllNotificationsRead)
    const openDetail = useCrmStore(state => state.openDetail)

    const unreadCount = unreadIds.length
    const visible = NOTIFICATIONS.filter(item => filter === 'all' || unreadIds.includes(item.id))

    function handleSelect(id: string, companySlug: string) {
        markRead(id)

        const company = companies.find(item => item.slug === companySlug)

        if (!company) return

        navigated.current = true
        setOpen(false)
        openDetail(company.id)
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant='secondary'
                    size='icon'
                    aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
                    className='data-[state=open]:bg-muted relative'
                >
                    <BellIcon aria-hidden className='size-3.5' />
                    {unreadCount > 0 && (
                        <span
                            aria-hidden
                            className='bg-danger ring-secondary absolute top-[7px] right-[7px] size-1.5 rounded-full ring-2'
                        />
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className='w-[min(400px,calc(100vw-2rem))]'
                onCloseAutoFocus={event => {
                    if (navigated.current) event.preventDefault()
                    navigated.current = false
                }}
            >
                <div className='flex h-14 items-center justify-between gap-2 px-4'>
                    <div className='flex items-center gap-2'>
                        <h2>Notifications</h2>
                        {unreadCount > 0 && <CountBadge>{unreadCount}</CountBadge>}
                    </div>
                    <Button
                        variant='ghost'
                        size='sm'
                        disabled={unreadCount === 0}
                        onClick={markAllRead}
                        className='-mr-1.5'
                    >
                        Mark all as read
                    </Button>
                </div>

                <Tabs value={filter} onValueChange={value => setFilter(value as Filter)}>
                    <TabsList className='border-line-strong border-b px-4'>
                        <TabsTrigger value='all' className='py-3'>
                            All
                        </TabsTrigger>
                        <TabsTrigger value='unread' className='py-3'>
                            Unread
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {visible.length > 0 ? (
                    <ScrollArea viewportClassName='max-h-[min(420px,60dvh)]'>
                        <ul className='flex flex-col gap-0.5 p-1.5'>
                            {visible.map(notification => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    company={companies.find(company => company.slug === notification.companySlug)}
                                    unread={unreadIds.includes(notification.id)}
                                    onSelect={() => handleSelect(notification.id, notification.companySlug)}
                                />
                            ))}
                        </ul>
                    </ScrollArea>
                ) : (
                    <div className='flex flex-col items-center gap-2 px-6 py-12 text-center'>
                        <span className='bg-muted flex size-10 items-center justify-center rounded-full shadow-[0px_0px_0px_1px_#232323]'>
                            <BellIcon aria-hidden className='text-soft size-4' />
                        </span>
                        <span className='lead-style mt-1 block font-medium'>
                            You’re all caught up
                        </span>
                        <span className='caption-style text-subtle block'>
                            New mentions and deal updates will show up here.
                        </span>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    )
}
